import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import LoadingScreen from './LoadingScreen';
import './ProtectedRoute.css';

/**
 * ProtectedRoute - Server-side authorized route wrapper
 * 
 * SECURITY: This component verifies authorization with the backend
 * before rendering protected content. No localStorage checks —
 * the server is the sole authority.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Component to render if authorized
 * @param {string} props.requiredRole - Required user role (e.g., 'admin')
 * @param {Array<string>} props.requiredRoles - Alternative: array of acceptable roles
 * @param {string} props.redirectTo - Path to redirect if unauthorized (default: '/')
 * @param {React.ReactNode} props.fallback - Custom loading component
 */
const ProtectedRoute = ({ 
  children, 
  requiredRole = null,
  requiredRoles = null,
  redirectTo = '/',
  fallback = null 
}) => {
  const [authState, setAuthState] = useState({
    loading: true,
    authorized: false,
    error: null
  });

  const backendURL = process.env.REACT_APP_TWCHATBOT_DOMAINS;

  useEffect(() => {
    const verifyAuthorization = async () => {
      try {
        const storedToken = typeof window !== 'undefined' ? sessionStorage.getItem('access_token') : null;
        const headers = {
          'Content-Type': 'application/json'
        };
        if (storedToken) {
          headers['Authorization'] = `Bearer ${storedToken}`;
        }

        // Verify authorization with backend using HttpOnly cookies / Bearer header
        const response = await fetch(`${backendURL}/api/protected/`, {
          method: 'GET',
          credentials: 'include', // Automatically attach HTTP-only cookies
          headers
        });

        if (!response.ok) {
          console.error('ProtectedRoute: Authorization verification failed', response.status);
          
          setAuthState({ 
            loading: false, 
            authorized: false, 
            error: `Authorization failed: ${response.status}` 
          });
          return;
        }

        const userData = await response.json();
        
        // Verify role from SERVER response (not localStorage)
        const userRole = userData.user?.role || userData.role;
        let isAuthorized = false;

        if (requiredRole) {
          isAuthorized = userRole === requiredRole;
        } else if (requiredRoles && Array.isArray(requiredRoles)) {
          isAuthorized = requiredRoles.includes(userRole);
        } else {
          // No role requirement, just need to be authenticated
          isAuthorized = true;
        }

        if (!isAuthorized) {
          console.warn(`ProtectedRoute: User role '${userRole}' not authorized. Required: ${requiredRole || requiredRoles}`);
        }

        setAuthState({ 
          loading: false, 
          authorized: isAuthorized, 
          error: isAuthorized ? null : 'Insufficient permissions' 
        });

      } catch (error) {
        console.error('ProtectedRoute: Authorization check error:', error);
        setAuthState({ 
          loading: false, 
          authorized: false, 
          error: error.message 
        });
      }
    };

    verifyAuthorization();
  }, [backendURL, requiredRole, requiredRoles]);

  // Show loading state
  if (authState.loading) {
    if (fallback) {
      return fallback;
    }
    
    return <LoadingScreen message="Verifying authorization" />;
  }

  // Redirect immediately when authorization fails.
  if (!authState.authorized) {
    console.warn('ProtectedRoute: Redirecting to', redirectTo);
    return <Navigate to={redirectTo} replace />;
  }

  // Render protected content
  return <>{children}</>;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredRole: PropTypes.string,
  requiredRoles: PropTypes.arrayOf(PropTypes.string),
  redirectTo: PropTypes.string,
  fallback: PropTypes.node,
};

export default React.memo(ProtectedRoute);
